import { configureApiClient } from '@t4s/api-client/fetch'
import type { QueryClient } from '@tanstack/react-query'
import { ApiFailure } from './api-error.ts'
import { createPlatformAuthGateway } from './gateway.ts'
import { endsTheSession } from './messages.ts'
import { createQueryClient } from './query-client.ts'
import { PlatformSessionStore } from './session.ts'

/**
 * Startup, in one function: the session store, the query client, and the generated
 * client pointed at an API with a credential and a 401 recovery.
 *
 * It runs **once, at module scope** (`main.tsx`), before React renders anything — which
 * is what keeps the query client from being rebuilt by a re-render (**CF1**, §5.3). A
 * test builds its own runtime instead of reaching for a singleton.
 */
export type Runtime = {
  readonly session: PlatformSessionStore
  readonly queryClient: QueryClient
}

export type RuntimeOptions = {
  /** Where the API is — see `api-origin.ts`. */
  readonly apiBaseUrl: string
  /** Overridable so a test can run the idle clock in milliseconds. */
  readonly idleTimeoutMs?: number
}

export function createRuntime(options: RuntimeOptions): Runtime {
  const session = new PlatformSessionStore({
    gateway: createPlatformAuthGateway(),
    ...(options.idleTimeoutMs === undefined ? {} : { idleTimeoutMs: options.idleTimeoutMs }),
  })

  const queryClient = createQueryClient({
    // A 401 that survived the transport's one refresh means the session is genuinely
    // over — an absolute expiry, a revoked session, a disabled account (§18.2). Ending
    // it here rather than on each screen is what makes "the session ended" one
    // behaviour instead of eleven.
    //
    // `forbidden` is deliberately not in that set: a 403 on a one-role surface is a
    // bug-shaped state, and signing the owner out over it would turn a server-side
    // mistake into a lockout.
    //
    // Not every such 401 is evidence: one from a request that started while the token was being
    // refreshed carries the token being replaced. The store judges which it is, and when the
    // session carries on, the reads that failed that way are asked again with the new token
    // (`fix-console-refresh-redirect-to-login`).
    onFailure: (failure) => {
      if (endsTheSession(failure.code)) {
        session.judgeUnauthorized(failure, () => {
          void queryClient.refetchQueries({
            type: 'active',
            predicate: (query) =>
              query.state.error instanceof ApiFailure && endsTheSession(query.state.error.code),
          })
        })
      }
    },
  })

  // Everything read for one administrator is thrown away when their session ends.
  //
  // The cache outlives the session — it is built once, at module scope (**CF1**) — and
  // nothing in the identity's key names *whose* identity it is, so without this a second
  // administrator signing in on the same tab is served the first one's nickname and full
  // name from cache: a flash while it refetches, and no refetch at all if they sign in
  // within the thirty-second `staleTime`, since nothing here polls or refetches on focus.
  // §4.5 makes that reachable rather than theoretical — "two accounts is the cheap
  // insurance" is how this console is meant to be operated.
  let signedIn = session.isSignedIn()
  session.subscribe(() => {
    const stillSignedIn = session.isSignedIn()
    if (signedIn && !stillSignedIn) {
      queryClient.clear()
    }
    signedIn = stillSignedIn
  })

  configureApiClient({
    baseUrl: options.apiBaseUrl,
    headers: session.authorizationHeaders,
    // An expired access token is refreshed once and the original request replays,
    // invisibly (§4.4). The transport guarantees the refresh call itself cannot enter
    // this path, so there is no exclusion list here to fall out of step — **CF2**.
    recoverFromUnauthorized: session.refresh,
  })

  return { session, queryClient }
}
